//core
import React from 'react';
import * as Yup from 'yup';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import Color from 'color';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Ramda from 'ramda';

//mui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/styles';

//components
import CbButton from '../../../../../components/Buttons/CbButton';
import { Show } from '../../../../../components/Show';
import TextFieldBase from '../../../../../components/inputs/text-fields/base/TextFieldBase';
import LanguageCurrencyFieldBase from '../../../../../components/inputs/LanguageCurrencyField';
import { Flexbox } from '../../../../../components/layout/Flexbox';

//hocs
import { withFormController } from '../../../../../components/hocs/forms';
import type { Languages } from '../../../../../i18n/i18n.language-config';

interface SpecificVendorEndorsementModalProps {
  data: {
    onSubmit: (formValues: SVFormValues) => void;
    vendors: SpecificVendor[];
    language: Languages;
  };
  close: () => void;
}

export const SpecificVendorEndorsementModal = ({
  data: modalProps,
  close,
}: SpecificVendorEndorsementModalProps) => {
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods = useForm<SVFormValues>({
    defaultValues: {
      vendors: modalProps.vendors,
    },
    resolver: yupResolver(schema),
  });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'vendors',
  });

  const handleAddVendor = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      name: '',
      rate: 0,
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    /* When component first mounts, check if at least one vendor exists. If so, dont add a card by default */
    if (formMethods.getValues().vendors[0]?.name) {
      return;
    }

    handleAddVendor({ scroll: false });
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(
          Ramda.compose(close, modalProps.onSubmit)
        )}
        noValidate
      >
        <DialogContent style={{ padding: '1rem 2.5rem' }}>
          <Show when={!fieldArrayMethods.fields.length}>
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="primary">
                No Specific Vendors
              </Typography>
            </Box>
          </Show>

          {fieldArrayMethods.fields.map((field, index) => {
            return (
              <CardContainer key={field.id} elevation={3}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Vendor Name"
                      required
                      name={`vendors.${index}.name`}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <LanguageCurrencyField
                      label="Rate"
                      required
                      language={modalProps.language}
                      name={`vendors.${index}.rate`}
                    />
                  </Grid>
                </Grid>
                <Button
                  className="contrast-text"
                  style={{ position: 'absolute', top: 5, right: 5 }}
                  onClick={() => fieldArrayMethods.remove(index)}
                >
                  Remove
                </Button>
              </CardContainer>
            );
          })}
          <div ref={scrollableRef} />
        </DialogContent>
        <DialogActions>
          <Flexbox>
            <Box>
              <Button
                startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
                className="contrast-text"
                onClick={() => handleAddVendor({ scroll: true })}
              >
                Add Vendor
              </Button>
            </Box>

            <Box>
              <CbButton onClick={close} styleName="cancel">
                Cancel
              </CbButton>
              <CbButton type="submit" styleName="ctaButton">
                Save
              </CbButton>
            </Box>
          </Flexbox>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default SpecificVendorEndorsementModal;

export const SpecificVendorEndorsementModalConfig = {
  SpecificVendorEndorsementModal: {
    component: SpecificVendorEndorsementModal,
    config: {
      title: 'Specific Vendor Endorsement',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};

//controlled inputs
const TextField = withFormController(TextFieldBase);
const LanguageCurrencyField = withFormController(LanguageCurrencyFieldBase);

//styling
const CardContainer = styled(Paper)(({ theme }) => {
  return {
    background:
      theme.palette.mode === 'dark'
        ? Color(theme.config.colors.darkGreyActive).darken(0.3).toString()
        : Color(theme.palette.background.active).lighten(0.065).toString(),
    padding: '2rem',
    borderRadius: 5,
    marginBottom: '2rem',
    position: 'relative',
  };
});

const stripSpecialChars = (_value: string, originalValue: string) => {
  if (!originalValue) {
    return 0;
  }

  return Number(String(originalValue)?.replace(/[^\d.]/g, ''));
};

//validations
const schema = Yup.object().shape({
  vendors: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().trim().label('Vendor name').required(),
      rate: Yup.number()
        .transform(stripSpecialChars)
        .min(0, 'Rate must be 0 or greater')
        .label('Rate')
        .required(),
    })
  ),
});

//types
export type SVFormValues = {
  vendors: SpecificVendor[];
};

export type SpecificVendor = {
  name: string;
  rate: number;
};
